// Shared geometry primitives for the anatomical body map
// (MUSCLE_BODY_MAP_PLAN.md, Phase 4). React-free and JSX-free on purpose:
// `scripts/validateMuscleMap.ts` imports the region modules directly, and they
// in turn import this file (hazard #7).
//
// The three views all draw on the same 220x520 canvas (D-BM-16) so switching
// between front / back / side never makes the figure jump.

import type { AnatomicalMuscle } from "@medassist/shared";

export const CANVAS_WIDTH = 220;
export const CANVAS_HEIGHT = 520;
/** Vertical centerline of the front/back figure. */
export const CENTER_X = CANVAS_WIDTH / 2;
export const VIEWBOX = `0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`;

/**
 * One tappable muscle region in one view.
 *
 * Paired muscles get two regions (`-l` / `-r`) that shade identically today —
 * `GymExercisePayload` has no `side` field, so per-side load is not knowable
 * (D-BM-8). The split paths are the seam that lets a later `side` field light
 * them independently with no artwork change.
 */
export interface BodyRegion {
  /** `${muscle}-l` | `${muscle}-r` | `${muscle}-c`. Unique within a view. */
  id: string;
  muscle: AnatomicalMuscle;
  side: "left" | "right" | "center";
  /** SVG path `d`, absolute coords in the 220x520 canvas. Must start with "M". */
  d: string;
}

/** Commands `mirrorPath` / `reversePath` understand, and how many x,y pairs each takes. */
const PAIR_COUNT: Record<string, number> = { M: 1, L: 1, T: 1, S: 2, Q: 2, C: 3 };

const fmt = (n: number): string => String(Math.round(n * 100) / 100);

function tokenize(d: string): string[] {
  const tokens = d.match(/[A-Za-z]|[-+]?(?:\d*\.\d+|\d+)/g);
  if (!tokens || tokens.length === 0) throw new Error(`bodymap: unparseable path "${d}"`);
  return tokens;
}

/**
 * Reflects an absolute-coordinate path across a vertical axis. Used to derive
 * every `-r` region from its hand-authored `-l` twin, so the two halves of the
 * figure are symmetric by construction rather than by careful retyping.
 *
 * Only absolute M/L/C/Q/S/T/Z are supported (that is all the artwork uses);
 * anything else throws at module load rather than silently producing a bad path.
 */
export function mirrorPath(d: string, axisX: number = CENTER_X): string {
  const tokens = tokenize(d);
  const out: string[] = [];
  let command = "";
  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i];
    if (/^[A-Za-z]$/.test(tok)) {
      command = tok.toUpperCase();
      if (tok !== command) throw new Error(`bodymap: relative command "${tok}" is not mirrorable in "${d}"`);
      i += 1;
      if (command === "Z") {
        out.push("Z");
        continue;
      }
      if (!(command in PAIR_COUNT)) throw new Error(`bodymap: unsupported command "${command}" in "${d}"`);
      out.push(command);
      continue;
    }
    const pairs = PAIR_COUNT[command];
    if (!pairs) throw new Error(`bodymap: coordinate before any command in "${d}"`);
    for (let p = 0; p < pairs; p += 1) {
      const x = Number(tokens[i]);
      const y = Number(tokens[i + 1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) throw new Error(`bodymap: bad coordinate pair in "${d}"`);
      out.push(fmt(2 * axisX - x), fmt(y));
      i += 2;
    }
  }
  return out.join(" ");
}

/**
 * Reverses an open absolute M/L/C chain so it runs end-to-start. Combined with
 * `mirrorPath` this turns a half-silhouette (traced down one side of the body
 * to a point on the centerline) into a closed, perfectly symmetric outline.
 */
export function reversePath(d: string): string {
  const tokens = tokenize(d);
  type Pt = [number, number];
  const segments: { command: "L" | "C"; points: Pt[] }[] = [];
  let start: Pt | null = null;
  let command = "";
  let i = 0;
  const take = (): Pt => {
    const p: Pt = [Number(tokens[i]), Number(tokens[i + 1])];
    if (!Number.isFinite(p[0]) || !Number.isFinite(p[1])) throw new Error(`bodymap: bad coordinate pair in "${d}"`);
    i += 2;
    return p;
  };
  while (i < tokens.length) {
    const tok = tokens[i];
    if (/^[A-Za-z]$/.test(tok)) {
      command = tok;
      i += 1;
      continue;
    }
    if (command === "M") {
      const p = take();
      if (start) throw new Error(`bodymap: reversePath does not support multiple subpaths in "${d}"`);
      start = p;
      command = "L"; // implicit lineto for any further pairs, per the SVG spec
    } else if (command === "L") {
      segments.push({ command: "L", points: [take()] });
    } else if (command === "C") {
      segments.push({ command: "C", points: [take(), take(), take()] });
    } else {
      throw new Error(`bodymap: reversePath only supports M/L/C, got "${command}" in "${d}"`);
    }
  }
  if (!start) throw new Error(`bodymap: path has no moveto: "${d}"`);
  const last = segments.length > 0 ? segments[segments.length - 1].points.slice(-1)[0] : start;
  const out: string[] = ["M", fmt(last[0]), fmt(last[1])];
  for (let s = segments.length - 1; s >= 0; s -= 1) {
    const previousEnd = s === 0 ? start : segments[s - 1].points.slice(-1)[0];
    const segment = segments[s];
    if (segment.command === "L") {
      out.push("L", fmt(previousEnd[0]), fmt(previousEnd[1]));
    } else {
      const [c1, c2] = segment.points;
      out.push("C", fmt(c2[0]), fmt(c2[1]), fmt(c1[0]), fmt(c1[1]), fmt(previousEnd[0]), fmt(previousEnd[1]));
    }
  }
  return out.join(" ");
}

/**
 * Closes a half-silhouette into a full symmetric outline: the chain must start
 * somewhere off the centerline and end ON it, so the mirrored return leg joins
 * seamlessly and `Z` closes the remaining gap across the top.
 */
export function mirrorClose(halfD: string, axisX: number = CENTER_X): string {
  const returnLeg = mirrorPath(reversePath(halfD), axisX);
  // Drop the return leg's own "M x y" — the pen is already at that point.
  const withoutMoveTo = returnLeg.replace(/^M\s+[-\d.]+\s+[-\d.]+\s*/, "");
  return `${halfD} ${withoutMoveTo} Z`;
}

/**
 * Midpoint of the axis-aligned bounding box of every coordinate pair in an
 * absolute SVG path `d` (control points included — this is not an area
 * centroid). Used to anchor the mini-map's muscle leader lines. Throws on an
 * unparseable path, same as mirrorPath.
 */
export function pathBoundsCenter(d: string): [number, number] {
  const tokens = tokenize(d);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let command = "";
  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i];
    if (/^[A-Za-z]$/.test(tok)) {
      command = tok.toUpperCase();
      i += 1;
      if (command === "Z") continue;
      if (!(command in PAIR_COUNT)) throw new Error(`bodymap: unsupported command "${command}" in "${d}"`);
      continue;
    }
    const pairs = PAIR_COUNT[command];
    if (!pairs) throw new Error(`bodymap: coordinate before any command in "${d}"`);
    for (let p = 0; p < pairs; p += 1) {
      const x = Number(tokens[i]);
      const y = Number(tokens[i + 1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) throw new Error(`bodymap: bad coordinate pair in "${d}"`);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      i += 2;
    }
  }
  if (!Number.isFinite(minX)) throw new Error(`bodymap: path has no coordinates: "${d}"`);
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}

/** Builds the `-l` region plus its mirrored `-r` twin from one hand-drawn path. */
export function pairRegions(muscle: AnatomicalMuscle, leftD: string): BodyRegion[] {
  return [
    { id: `${muscle}-l`, muscle, side: "left", d: leftD },
    { id: `${muscle}-r`, muscle, side: "right", d: mirrorPath(leftD) },
  ];
}

/** Builds a single unpaired, centered region. */
export function centerRegion(muscle: AnatomicalMuscle, d: string): BodyRegion {
  return { id: `${muscle}-c`, muscle, side: "center", d };
}
